import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Fingerprint, Lock, CheckCircle2, ShieldX } from "lucide-react";
import { requestDeviceBiometrics } from "@/components/register/PasskeySetup";

export const BiometricGate = ({ t, open, title, sub, successMsg, onClose, onAuthenticated, onFailed }) => {
  const [state, setState] = useState("idle");

  const authenticate = async () => {
    setState("checking");
    try {
      const ok = await requestDeviceBiometrics();
      if (!ok) throw new Error("biometric_failed");
      await onAuthenticated();
      setState("success");
      setTimeout(() => { setState("idle"); onClose(); }, 1400);
    } catch (e) {
      setState("failed");
      onFailed?.(e);
      setTimeout(() => { setState("idle"); onClose(); }, 2200);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#05050A]/60 backdrop-blur-xl px-6"
          data-testid="biometric-gate-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md bg-[#0F0F1A]/90 border border-white/10 rounded-3xl p-8 text-center"
          >
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#B026FF]/15 border border-[#B026FF]/40 flex items-center justify-center mb-5">
              <Lock size={26} className="text-[#D080FF]" />
            </div>
            <h2 className="font-heading text-lg font-black text-white mb-2">{title}</h2>
            <p className="text-sm text-[#A0A0B0] mb-8">{sub}</p>

            {state === "success" ? (
              <motion.p initial={{ scale: 0.8 }} animate={{ scale: 1 }} data-testid="gate-success-msg"
                className="text-[#00FFAA] font-bold text-sm flex items-center justify-center gap-2 py-3">
                <CheckCircle2 size={18} /> {successMsg}
              </motion.p>
            ) : state === "failed" ? (
              <p data-testid="gate-failed-msg" className="text-red-400 font-bold text-sm flex items-center justify-center gap-2 py-3">
                <ShieldX size={18} /> {t.restricted}
              </p>
            ) : (
              <>
                <button data-testid="gate-authenticate-btn" onClick={authenticate} disabled={state === "checking"}
                  className="w-full py-4 rounded-full font-bold text-xs uppercase tracking-widest bg-[#B026FF] shadow-[0_0_20px_rgba(176,38,255,0.45)] flex items-center justify-center gap-2 disabled:opacity-70">
                  <Fingerprint size={18} className={state === "checking" ? "animate-pulse" : ""} />
                  {state === "checking" ? t.authenticating : t.authenticate}
                </button>
                <button data-testid="gate-cancel-btn" onClick={() => { setState("idle"); onClose(); }}
                  className="mt-4 text-xs text-white/40 hover:text-white/70 uppercase tracking-widest font-bold">
                  {t.cancel}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
